// ---- Constants ----
const COMPARE_KEY = 'shopify_compare_products';
const MAX_COMPARE = 2;
const SHAKE_ANIMATION_DURATION_MS = 400;
const SLOT_TRANSITION_DURATION_MS = 250; // must match CSS transition duration

const DEFAULT_ADD_TO_CART_LABEL = 'Add to cart';
const DEFAULT_ADD_TO_CART_LOADING_LABEL = 'Adding...';
const ROTATE_OVERLAY_BREAKPOINT_PX = 900;
const RESIZE_DEBOUNCE_MS = 150;

const LABELS = {
  error: 'Błąd — spróbuj ponownie',
  added: 'Dodano ✓',
};
const LABELS_QUICK = {
  loading: '...',
  added: '✓'
}

const SELECTORS = {
  compareModal: '[data-compare-modal]',
  compareBar: '[data-compare-bar]',
  compareToggle: '[data-compare-toggle]',
  compareSlot: '[data-compare-slot]',
  compareOpen: '[data-compare-open]',
  compareColumns: '[data-compare-columns]',
  compareClose: '[data-compare-close]',
  compareSlider: '[data-compare-slider]',
  sliderPrev: '.compare-slider__prev',
  sliderNext: '.compare-slider__next',
  sliderDot: '.compare-slider__dot',
  addToCart: '[data-compare-add-to-cart]',
  compareRotateOverlay: '[data-compare-rotate-overlay]',
};

// ---- State helpers ----
const getCompareList = () => JSON.parse(localStorage.getItem(COMPARE_KEY) || '[]');

const saveCompareList = (list) => {
  localStorage.setItem(COMPARE_KEY, JSON.stringify(list));
  updateCompareUI();
};

function toggleCompare(productHandle) {
  if (!productHandle) return;

  let list = getCompareList();
  const wasInList = list.includes(productHandle);

  if (wasInList) {
    list = list.filter((handle) => handle !== productHandle);
  } else {
    if (list.length >= MAX_COMPARE) {
      showCompareLimitMessage();
      return;
    }
    list.push(productHandle);
  }

  saveCompareList(list);

  // Removing a product from an open modal leaves only 1 item, which can't be
  // compared, so we close the modal and send the user back to the picker bar.
  const modal = getCompareModal();
  if (wasInList && modal?.open) {
    closeCompareModal();
  }
}

function showCompareLimitMessage() {
  const bar = document.querySelector(SELECTORS.compareBar);
  if (!bar) return;

  bar.classList.add('is-shake');
  setTimeout(() => bar.classList.remove('is-shake'), SHAKE_ANIMATION_DURATION_MS);
}

// ---- Data fetching ----
const LOCALES = ['en', 'de', 'fr', 'pl'];

const tryFetchProduct = async (url) => {
  try {
    const res = await fetch(url);
    return res.ok ? await res.json() : null;
  } catch (err) {
    // console.error(`Compare: failed to fetch "${url}"`, err);
    return null;
  }
};

const fetchProduct = async (handle) => {
  const product = await tryFetchProduct(`/products/${handle}.js`);
  if (product) return product;

  // Default URL missed — the handle likely belongs to another locale. Try
  // them all in parallel and take the first one that resolves.
  const localeResults = await Promise.allSettled(
    LOCALES.map((locale) => tryFetchProduct(`/${locale}/products/${handle}.js`))
  );

  const firstMatch = localeResults.find((result) => result.status === 'fulfilled' && result.value);
  if(!firstMatch) {
    toggleCompare(handle)
    return null;
  }
  return firstMatch.value;
};

const fetchProducts = (handles) => Promise.all(handles.map(fetchProduct));

// ---- Security helper ----
const escapeHtml = (value = '') =>
  String(value).replace(
    /[&<>"']/g,
    (char) =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      }[char])
  );

// ---- Compare bar / product card UI ----
async function updateCompareUI() {
  const list = getCompareList();

  updateToggleButtonsState(list);
  updateCompareBarVisibility(list);
  await updateCompareSlots(list);

  const openBtn = document.querySelector(SELECTORS.compareOpen);
  if (openBtn) openBtn.disabled = list.length < MAX_COMPARE;
}

// ---- Rotate overlay ----
const rotatePortraitQuery = window.matchMedia('(orientation: portrait)');

const isBelowRotateBreakpoint = () => {
  const isNarrowViewport = window.innerWidth < ROTATE_OVERLAY_BREAKPOINT_PX;
  const isPortrait = rotatePortraitQuery.matches;
  return isNarrowViewport && isPortrait;
};

function updateRotateOverlayVisibility() {
  const overlay = getCompareModal()?.querySelector(SELECTORS.compareRotateOverlay);
  if (!overlay) return;
  overlay.classList.toggle('is-visible', isBelowRotateBreakpoint());
}

// Simple debounce so resize doesn't trigger a toggle on every pixel.
function debounce(fn, delay) {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

const debouncedUpdateRotateOverlayVisibility = debounce(updateRotateOverlayVisibility, RESIZE_DEBOUNCE_MS);

function updateToggleButtonsState(list) {
  // Matches every toggle currently in the DOM, including cards loaded later
  // via AJAX/infinite scroll.
  document.querySelectorAll(SELECTORS.compareToggle).forEach((btn) => {
    const handle = btn.dataset.compareToggle;
    const isActive = list.includes(handle);
    btn.classList.toggle('is-active', isActive);
    btn.classList.toggle('is-disabled', !isActive && list.length >= MAX_COMPARE);
  });
}

function updateCompareBarVisibility(list) {
  const bar = document.querySelector(SELECTORS.compareBar);
  if (bar) bar.classList.toggle('is-visible', list.length > 0);
}

async function updateCompareSlots(list) {
  let products = [];
  if (list.length > 0) {
    try {
      products = await fetchProducts(list);
    } catch (err) {
      console.error('Compare: failed to fetch product data', err);
    }
  }

  document.querySelectorAll(SELECTORS.compareSlot).forEach((slot, i) => {
    const product = products[i];
    const existingContent = slot.querySelector('.compare-slot__content');
    const currentHandle = existingContent?.dataset.handle;

    if (product && currentHandle === product.handle) return; // already rendered

    if (existingContent) {
      existingContent.classList.remove('is-visible');
      setTimeout(() => fillSlot(slot, product), SLOT_TRANSITION_DURATION_MS);
    } else {
      fillSlot(slot, product);
    }
  });
}

function fillSlot(slot, product) {
  slot.classList.toggle('is-filled', !!product);

  if (!product) {
    slot.innerHTML = '';
    return;
  }

  // Preload the image so it appears together with the "remove" icon instead
  // of the icon showing first and the image popping in later.
  const preloadImg = new Image();
  preloadImg.src = product.featured_image;

  const renderSlot = () => {
    slot.innerHTML = buildCompareSlotMarkup(product);
    const content = slot.querySelector('.compare-slot__content');
    requestAnimationFrame(() => content.classList.add('is-visible'));
  };

  if (preloadImg.complete) {
    renderSlot();
  } else {
    preloadImg.onload = renderSlot;
    preloadImg.onerror = renderSlot;
  }
}

const buildCompareSlotMarkup = (product) => `
  <div class="compare-slot__content" data-handle="${escapeHtml(product.handle)}">
    <img src="${product.featured_image}" alt="${escapeHtml(product.title)}" loading="lazy">
    <span class="compare-slot__remove" data-compare-toggle="${escapeHtml(product.handle)}" role="button" aria-label="Remove from comparison">&times;</span>
  </div>
`;

// ---- Compare modal ----
const getCompareModal = () => document.querySelector(SELECTORS.compareModal);

function getAddToCartLabels() {
  const { buttonAddToCart, buttonAddToCartLoading } = getCompareModal()?.dataset || {};
  return {
    addToCart: buttonAddToCart || DEFAULT_ADD_TO_CART_LABEL,
    addToCartLoading: buttonAddToCartLoading || DEFAULT_ADD_TO_CART_LOADING_LABEL,
  };
}

async function openCompareModal() {
  const handles = getCompareList();
  if (handles.length < MAX_COMPARE) return;

  let products;
  try {
    products = await fetchProducts(handles);
  } catch (err) {
    console.error('Compare: failed to fetch product data for modal', err);
    return;
  }

  renderCompareModal(products);
}

function renderCompareModal(products) {
  const modal = getCompareModal();
  if (!modal) return;

  const validProducts = products.filter(Boolean);
  if (validProducts.length < MAX_COMPARE) {
    console.error('Compare: one or more products could not be loaded, aborting modal render');
    return;
  }

  const container = modal.querySelector(SELECTORS.compareColumns);
  container.innerHTML = validProducts.map(buildProductColumnMarkup).join('');

  updateRotateOverlayVisibility(); // check orientation state before showing
  modal.showModal();
}

function buildProductColumnMarkup(product) {
  const { addToCart } = getAddToCartLabels();

  return `
    <div class="compare-column">
      <h3>${escapeHtml(product.title)}</h3>
      ${buildSliderMarkup(product.images, product.title)}
      <div class="compare-column__footer">
        <p class="price">${formatMoney(product.price)}</p>
        <button type="button" class="component-button" data-compare-add-to-cart="${product.variants[0].id}">
          <div class="component-button__inner" tabindex="-1">
            <div class="component-button__label-wrapper">
              <span class="component-button__label">${escapeHtml(addToCart)}</span>
              <span class="component-button__label-secondary" aria-hidden="true">${escapeHtml(addToCart)}</span>
            </div>
          </div>
        </button>
      </div>
    </div>
  `;
}

// Shared by both the full-size slides and the thumbnail dots — they're the
// same <img> shape, just with different classes/attributes.
function buildSlideImage({ src, alt, index, isActive, className, extraAttrs = '' }) {
  return `
    <img
      src="${src}"
      alt="${escapeHtml(alt)}"
      loading="lazy"
      class="${className} ${isActive ? 'is-active' : ''}"
      data-slide-index="${index}"
      ${extraAttrs}
    >
  `;
}

function buildSliderMarkup(images, title) {
  const slides = images
    .map((img, i) =>
      buildSlideImage({ src: img, alt: title, index: i, isActive: i === 0, className: 'compare-slider__image' })
    )
    .join('');

  const controls =
    images.length > 1
      ? `
    <button type="button" class="compare-slider__prev" aria-label="Previous image"><span>&#8249;</span></button>
    <button type="button" class="compare-slider__next" aria-label="Next image"><span>&#8250;</span></button>
    <div class="compare-slider__dots">
      ${images
        .map((img, i) =>
          buildSlideImage({
            src: img,
            alt: title,
            index: i,
            isActive: i === 0,
            className: 'dot-img compare-slider__dot',
            extraAttrs: `width="100" height="80" role="button" tabindex="0" aria-label="Show image ${i + 1}"`,
          })
        )
        .join('')}
    </div>
  `
      : '';

  return `
    <div class="compare-slider" data-compare-slider data-current="0">
      <div class="compare-slider__track">${slides}</div>
      ${controls}
    </div>
  `;
}

function closeCompareModal() {
  const modal = getCompareModal();
  if (modal?.open) modal.close();
}

function formatMoney(cents) {
  if (window?.theme?.formatMoney) {
    return window.theme.formatMoney(cents);
  }
  return `${(cents / 100).toFixed(2)}`;
}

// ---- Slider controls ----
function setActiveSlide(sliderEl, index) {
  if (!sliderEl) return;

  const images = sliderEl.querySelectorAll('.compare-slider__image');
  const dots = sliderEl.querySelectorAll('.compare-slider__dot');

  images.forEach((img) => img.classList.toggle('is-active', Number(img.dataset.slideIndex) === index));
  dots.forEach((dot) => dot.classList.toggle('is-active', Number(dot.dataset.slideIndex) === index));

  sliderEl.dataset.current = index;
}

function changeSlide(sliderEl, direction) {
  if (!sliderEl) return;

  const total = sliderEl.querySelectorAll('.compare-slider__image').length;
  if (total <= 1) return;

  const current = Number(sliderEl.dataset.current) || 0;
  const nextIndex = (current + direction + total) % total;
  setActiveSlide(sliderEl, nextIndex);
}

// ---- Add to cart ----
function setButtonLabel(btn, text) {
  const label = btn.querySelector('.component-button__label');
  const labelSecondary = btn.querySelector('.component-button__label-secondary');
  if (label) label.textContent = text;
  if (labelSecondary) labelSecondary.textContent = text;
}

const getCartElement = () => document.querySelector('cart-notification') || document.querySelector('cart-drawer');

function buildAddToCartFormData(variantId, cart) {
  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', 1);

  if (typeof cart?.getSectionsToRender === 'function') {
    formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
    formData.append('sections_url', window.location.pathname);
    cart.setActiveElement?.(document.activeElement);
  }

  return formData;
}

const submitAddToCart = (formData) =>
  fetch(window.routes?.cart_add_url || '/cart/add.js', {
    method: 'POST',
    headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
    body: formData,
  }).then((res) => res.json());

function syncCartDrawerAfterAdd(cart, data) {
  // The comparison <dialog> renders in the top layer above everything
  // (regardless of z-index), so it must close before the drawer renders.
  closeCompareModal();

  try {
    cart.renderContents(data);
  } catch (err) {
    console.error('Compare: cart.renderContents failed', err);
  }

  // renderContents() only clears "is-empty" on .drawer__inner, not on the
  // <cart-drawer> host itself.
  cart.classList.remove('is-empty');

  replaceCartDrawerIfMissing(data);
}

function replaceCartDrawerIfMissing(data) {
  requestAnimationFrame(() => {
    const drawerMarkup = data.sections?.['cart-drawer'];
    if (document.querySelector('#CartDrawer') || !drawerMarkup) return;

    const newDrawer = new DOMParser().parseFromString(drawerMarkup, 'text/html').querySelector('cart-drawer');
    const oldDrawer = document.querySelector('cart-drawer');
    if (!newDrawer || !oldDrawer) return;

    newDrawer.classList.remove('is-empty');
    oldDrawer.replaceWith(newDrawer);
    newDrawer.open?.();
  });
}

async function addCompareItemToCart(addBtn) {
  const variantId = addBtn.dataset.compareAddToCart;
  const { addToCartLoading } = getAddToCartLabels();
  addBtn.disabled = true;
  const loadingLabel = addBtn.classList.contains('quick_add') ? LABELS_QUICK.loading : addToCartLoading;
  setButtonLabel(addBtn, loadingLabel);
  const cart = getCartElement();
  const formData = buildAddToCartFormData(variantId, cart);

  try {
    const data = await submitAddToCart(formData);

    if (data.status) {
      console.error('Compare: cart error', data);
      setButtonLabel(addBtn, LABELS.error);
      return;
    }

    if (!cart || typeof cart.renderContents !== 'function') {
      window.location = window.routes?.cart_url || '/cart';
      return;
    }

    syncCartDrawerAfterAdd(cart, data);
    const addLabel = addBtn.classList.contains('quick_add') ? LABELS_QUICK.added : LABELS_QUICK.added;
    setButtonLabel(addBtn, addLabel);
  } catch (err) {
    console.error('Compare: add to cart failed', err);
    setButtonLabel(addBtn, LABELS.error);
  } finally {
    addBtn.disabled = false;
  }
}

// ---- Event delegation ----
const CLICK_HANDLERS = [
  { selector: SELECTORS.compareToggle, preventDefault: true, handle: (el) => toggleCompare(el.dataset.compareToggle) },
  { selector: SELECTORS.compareOpen, handle: () => openCompareModal() },
  { selector: SELECTORS.compareClose, handle: () => closeCompareModal() },
  { selector: SELECTORS.sliderPrev, handle: (el) => changeSlide(el.closest(SELECTORS.compareSlider), -1) },
  { selector: SELECTORS.sliderNext, handle: (el) => changeSlide(el.closest(SELECTORS.compareSlider), 1) },
  {
    selector: SELECTORS.sliderDot,
    handle: (el) => setActiveSlide(el.closest(SELECTORS.compareSlider), Number(el.dataset.slideIndex)),
  },
  { selector: SELECTORS.addToCart, preventDefault: true, handle: (el) => addCompareItemToCart(el) },
];

document.addEventListener('click', async (event) => {
  for (const { selector, preventDefault, handle } of CLICK_HANDLERS) {
    const target = event.target.closest(selector);
    if (!target) continue;
    if (preventDefault) event.preventDefault();
    await handle(target);
    return;
  }

  const modal = getCompareModal();
  if (modal && event.target === modal) closeCompareModal(); // click on <dialog> backdrop
});

// The dots got role="button"/tabindex="0" for keyboard users, so Enter/Space
// need to trigger the same action as a click would.
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;

  const dot = event.target.closest(SELECTORS.sliderDot);
  if (!dot) return;

  event.preventDefault();
  setActiveSlide(dot.closest(SELECTORS.compareSlider), Number(dot.dataset.slideIndex));
});

document.addEventListener('DOMContentLoaded', () => {
  updateCompareUI();

  window.addEventListener('resize', debouncedUpdateRotateOverlayVisibility);
  rotatePortraitQuery.addEventListener('change', updateRotateOverlayVisibility);

  const compareGridObserver = new MutationObserver((mutations) => {
    const hasNewToggle = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some(
        (node) => node.nodeType === 1 && (node.matches?.(SELECTORS.compareToggle) || node.querySelector?.(SELECTORS.compareToggle))
      )
    );

    if (hasNewToggle) updateCompareUI();
  });

  compareGridObserver.observe(document.body, { childList: true, subtree: true });
});